import NumberInput from '@/ui/number'
import Tooltip from '@/ui/tooltip'
import { formatNumber } from '@/utils/format'
import classNames from 'classnames'
import React from 'react'

export default function HoverNumber({
  disabled,
  value = 0,
  onAccept,
  style,
  className,
  ...props
}: Omit<React.ComponentProps<typeof NumberInput>, 'onEnter'> & {
  onAccept?: () => void
}) {
  const [editing, setEditing] = React.useState<boolean | 'focus'>(false)
  const [hover, setHover] = React.useState(false)

  const onMouseOver = React.useCallback(() => setHover(true), [])
  const onMouseOut = React.useCallback(() => setHover(false), [])

  const inputRef = React.useRef<HTMLInputElement>(null)

  const onFocusSpan = React.useCallback(() => setEditing('focus'), [])
  React.useEffect(() => {
    if (editing === 'focus') inputRef.current?.focus()
  }, [editing])

  const onFocus = React.useCallback(() => setEditing(true), [])
  const onBlur = React.useCallback(() => {
    setEditing(false)
    if (onAccept) onAccept()
  }, [onAccept])

  if ((props.min != null && props.max != null) || props.values) {
    const max = props.values
      ? Math.max(...props.values.map((x) => Math.abs(x)))
      : Math.max(Math.abs(Number(props.min)), Math.abs(Number(props.max)))
    let digits = Math.floor(Math.log10(max)) + 1
    if (props.digits !== 0 && props.step && props.step !== 1) {
      digits += 0.6 + (props.digits || 2)
    }
    style = {
      ...style,
      width: `${(0.6 * digits + 2).toFixed(1)}em`
    }
  }

  const expanded = !disabled && (hover || editing)

  const format = (x: number) =>
    formatNumber(x, props.digits ?? 2, null, props.trim !== false)

  const content = (
    <span
      className={classNames('poe-HoverNumber', className, {
        'poe-disabled': disabled
      })}
      tabIndex={expanded ? undefined : 0}
      onMouseEnter={onMouseOver}
      onMouseLeave={onMouseOut}
      onFocus={onFocusSpan}>
      {format(value)}
      {!!expanded && (
        <NumberInput
          className="poe-HoverInput"
          value={value}
          onFocus={onFocus}
          onEnter={onAccept}
          onBlur={onBlur}
          style={style}
          ref={inputRef}
          {...props}
        />
      )}
    </span>
  )

  if (props.min != null && props.max != null && !disabled) {
    return (
      <Tooltip
        tooltip={`${format(Number(props.min))}\u2013${format(
          Number(props.max)
        )}`}
        align="left">
        {content}
      </Tooltip>
    )
  }

  return content
}
